import { FC, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Bold, Paragraph } from "capitalrx-components-2.0/lib/typography";
import { formatDate } from "capitalrx-utilities";

import { useListMemberNotificationsQuery } from "@/App/api/notification";
import { ContentPanel, NoResults, PageWrapper, Pager, DependentDropdown } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

import styled from "styled-components";
import { useListMemberDependentsQuery } from "@/App/api/member";
import { MemberEligibilityContext } from "@/App/context";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { useSearchParams } from "react-router";

const MessageRow = styled.div`
  border-bottom: 1px solid #cfd9e1;
  padding: 16px;

  &:hover {
    cursor: pointer;
    background-color: rgba(165, 202, 235, 0.1);
  }
`;

export const InboxPage: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const [pageNumber, setPageNumber] = useState(1);

  const personCode = useMemo(() =>
    searchParams.get("personCode") ?? memberEligibility.member.person_code,
  [memberEligibility, searchParams]);

  const { data: allMessages, isLoading: isLoadingMessages } = useListMemberNotificationsQuery({
    page_number: pageNumber,
    results_per_page: 10,
    external_member_id: memberEligibility.member.external_member_id,
    client_id: memberEligibility.plan.client_id,
    person_code: personCode,
  });

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const ContentFlowContainer = isCompressed ? Stack : Shelf;

  const { data: dependents } = useListMemberDependentsQuery();

  const minorDependents = useMemo(() => {
      return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
    }, [dependents]);

  const hasDependents = useMemo(() =>
      !isViewingDataholder && minorDependents.length > 0, [isViewingDataholder, minorDependents]);

  const messages = useMemo(() => {
    return (allMessages?.results || []).map((m) => m.member_notification);
  }, [allMessages]);

  const onDependentFilterChange = (value: string) => {
    logEvent(ANALYTIC_EVENTS.INBOX.dependentFilterChange, { personCode: value });
    setSearchParams(`?personCode=${value}`);
  };

  const onNotificationSelected = useCallback((notificationId: number) => {
    navigate(`/inbox/${personCode}/${notificationId}`);
  }, [personCode]);

  return (
    <PageWrapper>
      <ContentPanel title={translate("INBOX_PAGE.messages")} showBorder={false}>
        {hasDependents && (
          <Stack spacing="large">
            <DependentDropdown
              key="inbox-dependents-view-dropdown"
              testId="inbox-dependents-view-dropdown"
              onChange={onDependentFilterChange}
              value={personCode}
              label={translate("INBOX_PAGE.viewMessagesFor")}
              aria-label={translate("INBOX_PAGE.viewMessagesFor")}
              dependents={minorDependents}
              member={memberEligibility.member}
            />
          </Stack>
        )}
        <LoadingGroup loading={isLoadingMessages}>
          <LoadingGroup.Placeholder
            fallback={
              <Shelf>
                <Shelf.Spacer />
                <Spinner />
                <Shelf.Spacer />
              </Shelf>
            }
          >
            <Stack>
              <Stack spacing="none">
                {messages.map((m) => {
                  return (
                    <MessageRow key={m.id} onClick={() => onNotificationSelected(m.id)}>
                      <ContentFlowContainer>
                        {m.read_status === "unread" && <Bold testId={`message-${m.id}-subject`}>{m.subject.startsWith("MESSAGE_DETAIL.") ? translate(m.subject) : m.subject}</Bold>}
                        {m.read_status !== "unread" && <Paragraph testId={`message-${m.id}-subject`}>{m.subject.startsWith("MESSAGE_DETAIL.") ? translate(m.subject) : m.subject}</Paragraph>}
                        <Shelf.Spacer />
                        {formatDate(m.created_at, "MMMM D, YYYY")}
                      </ContentFlowContainer>
                    </MessageRow>
                  );
                })}
                {messages.length === 0 && <NoResults message={translate("INBOX_PAGE.youHaveNoMessages")} />}
              </Stack>
              {messages.length > 0 && (
                <Shelf>
                  <Shelf.Spacer />
                  <Pager
                    pageNumber={pageNumber}
                    setPageNumber={setPageNumber}
                    disabled={isLoadingMessages}
                    resultCount={allMessages?.result_count}
                    resultsPerPage={10}
                  />
                  <Shelf.Spacer />
                </Shelf>
              )}
            </Stack>
          </LoadingGroup.Placeholder>
        </LoadingGroup>
      </ContentPanel>
    </PageWrapper>
  );
};
